import React, { useEffect, useState } from 'react';

function ExpandIcon() {
    return (
        <svg className="snapshot-narrative-expand-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 3H3v5M3 3l6 6M16 3h5v5M21 3l-6 6M8 21H3v-5M3 21l6-6M16 21h5v-5M21 21l-6-6" />
        </svg>
    );
}

function NarrativeToggle({ value, onChange }) {
    return (
        <div className="snapshot-narrative-toggle" role="group" aria-label="AI narrative detail level">
            {['basic', 'detailed'].map((mode) => (
                <button
                    type="button"
                    key={mode}
                    className={value === mode ? 'active' : ''}
                    aria-pressed={value === mode}
                    onClick={() => onChange(mode)}
                >
                    {mode === 'basic' ? 'Basic' : 'Detailed'}
                </button>
            ))}
        </div>
    );
}

function updatedLabel(value) {
    const timestamp = new Date(value).getTime();
    if (Number.isNaN(timestamp)) return null;
    const diff = Math.max(0, Date.now() - timestamp);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    if (minutes < 1) return 'Updated just now';
    if (minutes < 60) return `Updated ${minutes}m ago`;
    return `Updated ${hours}h ago`;
}

function ExpandedNarrative({ insights, viewMode }) {
    const basicSummary = insights.basic_summary || insights.summary;
    const detailedSummary = insights.detailed_summary || basicSummary;
    const summary = viewMode === 'detailed' ? detailedSummary : basicSummary;
    const drivers = Array.isArray(insights.top_drivers) ? insights.top_drivers : [];

    return (
        <div className="snapshot-narrative-report">
            <section>
                <h3>{viewMode === 'detailed' ? 'Detailed overview' : 'Fleet overview'}</h3>
                <p>{summary}</p>
            </section>
            {insights.affected_device_scope && (
                <section>
                    <h3>Affected fleet</h3>
                    <p>{insights.affected_device_scope}</p>
                </section>
            )}
            {viewMode === 'detailed' && drivers.length > 0 && (
                <section>
                    <h3>Top drivers</h3>
                    <ul>
                        {drivers.map((driver, index) => <li key={`${driver}-${index}`}>{driver}</li>)}
                    </ul>
                </section>
            )}
        </div>
    );
}

export default function FleetNarrativePanel({ snapshot, selectedDuration, onRetry }) {
    const [viewMode, setViewMode] = useState('basic');
    const [isExpanded, setIsExpanded] = useState(false);
    const insights = snapshot.data?.insights || {};
    const basicSummary = insights.basic_summary || insights.summary;
    const detailedSummary = insights.detailed_summary || basicSummary;
    const displayedSummary = viewMode === 'detailed' ? detailedSummary : basicSummary;

    useEffect(() => {
        setViewMode('basic');
        setIsExpanded(false);
    }, [selectedDuration]);

    useEffect(() => {
        if (!isExpanded) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setIsExpanded(false);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isExpanded]);

    return (
        <div className="snapshot-narrative-card">
            <div className="snapshot-narrative-header">
                <div className="snapshot-narrative-label">✨ AI Narrative</div>
                {snapshot.data && (
                    <div className="snapshot-narrative-actions">
                        <NarrativeToggle value={viewMode} onChange={setViewMode} />
                        <button
                            type="button"
                            className="snapshot-narrative-expand-button"
                            onClick={() => setIsExpanded(true)}
                            aria-label="Expand AI narrative"
                            title="Expand AI narrative"
                        >
                            <ExpandIcon />
                        </button>
                    </div>
                )}
            </div>

            {snapshot.loading ? (
                <div className="snapshot-loading">
                    <span className="snapshot-spinner" />
                    <span>{snapshot.warming || 'Generating fleet insight…'}</span>
                </div>
            ) : snapshot.error ? (
                <div className="snapshot-error-inline">
                    <span>{snapshot.error}</span>
                    <button className="snapshot-retry-btn" onClick={onRetry}>Retry</button>
                </div>
            ) : snapshot.data ? (
                <>
                    <p className={`snapshot-summary${viewMode === 'detailed' ? ' snapshot-summary-preview' : ''}`}>
                        {displayedSummary}
                    </p>
                    {insights.affected_device_scope && (
                        <p className="snapshot-scope">{insights.affected_device_scope}</p>
                    )}
                    {viewMode === 'detailed' && (
                        <button
                            type="button"
                            className="snapshot-narrative-expand-link"
                            onClick={() => setIsExpanded(true)}
                        >
                            Expand for full report
                        </button>
                    )}
                    <div className="snapshot-narrative-footer">
                        {snapshot.data.cache_metadata?.cached_at && (
                            <span className="snapshot-updated">
                                {updatedLabel(snapshot.data.cache_metadata.cached_at)}
                            </span>
                        )}
                    </div>
                </>
            ) : null}

            {isExpanded && snapshot.data && (
                <div className="snapshot-narrative-modal-overlay" role="presentation" onClick={() => setIsExpanded(false)}>
                    <div
                        className="snapshot-narrative-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="snapshot-narrative-modal-title"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="snapshot-narrative-modal-header">
                            <div>
                                <h2 id="snapshot-narrative-modal-title">AI Narrative</h2>
                                <p>Fleet health report for the selected lookback period</p>
                            </div>
                            <div className="snapshot-narrative-modal-actions">
                                <NarrativeToggle value={viewMode} onChange={setViewMode} />
                                <button type="button" onClick={() => setIsExpanded(false)} aria-label="Close AI narrative">×</button>
                            </div>
                        </div>
                        <div className="snapshot-narrative-modal-body">
                            <ExpandedNarrative insights={insights} viewMode={viewMode} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
